import _ from 'lodash';
import { IdentifyBodyLocationEnum } from 'src/utils/jmrUtils';
import { BackgroundFormData } from 'src/store/newJmrPageSlice/types';
import { JmrTask } from 'src/store/newJmrPageSlice/types';
import { isEmpty, isNotEmpty } from 'src/utils/helpers';
import { RegionTask } from 'src/services/tasks/types';
import { MeasureUnitAbbreviations, MeasureUnitValues } from 'src/services/tasks/constants';

// **** Constants **** //

export const WEIGHT_RESTRICTION_RANGE_NOT_SET = -1;

const VALIDATION_ERRORS = {
  MaximumWeightRangeRequired: 'Maximum weight range is required',
  NextRangeValueTooHigh:
    'Task of a higher maximum weight range cannot have greater hours than a task with a lower maximum range',
  NoTaskValue: 'A value must be selected',
} as const;

const WEIGHT_OPTIONS = {
  'up to 5': { header: 'Up to 5 lbs', value: 5 },
  '6 - 10': { header: 'Up to 10 lbs', value: 10 },
  '11 - 15': { header: 'Up to 15 lbs', value: 15 },
  '16 - 20': { header: 'Up to 20 lbs', value: 20 },
  '21 - 30': { header: 'Up to 30 lbs', value: 30 },
  '31 - 40': { header: 'Up to 40 lbs', value: 40 },
  '41 - 50': { header: 'Up to 50 lbs', value: 50 },
} as const;

// Putting the weight restriction stuff in one object so I can make sure the values
// match.
export const WeightRestrictionMaxRangeHandler = {
  getParentOptions(parentLabel: string): { label: string; value: number }[] {
    return [
      { label: `0+ lbs (Associate cannot ${parentLabel.toLowerCase()})`, value: 0 },
      { label: '5+ lbs', value: 5 },
      { label: '10+ lbs', value: 10 },
      { label: '15+ lbs', value: 15 },
      { label: '20+ lbs', value: 20 },
      { label: '30+ lbs', value: 30 },
      { label: '40+ lbs', value: 40 },
      { label: '50+ lbs', value: 50 },
    ];
  },
  getChildOption(taskLabel: string): { header: string; value: number } {
    const lowerCaseLabel = taskLabel.toLowerCase();
    const key = Object.keys(WEIGHT_OPTIONS).find((key) => lowerCaseLabel.includes(key));
    return key ? WEIGHT_OPTIONS[key as keyof typeof WEIGHT_OPTIONS] : { header: '', value: 0 };
  },
} as const;

// **** Types **** //

export type RestrictionListItem = RestrictionListTask | RestrictionListParentTask;
export type PreSelectedTask = Pick<
  JmrTask,
  'label' | 'restrictedValue' | 'taskId' | 'measureUnit' | 'bodyLocationValue'
>;

export interface RestrictionListTask extends RegionTask {
  restrictedValue?: string;
  bodyLocationValue?: IdentifyBodyLocationEnum;
  error?: string;
}

// Created this in case in the future there are consolidated tasks that aren't necessarily
// related to weight-restrictions.
export interface RestrictionListParentTask extends Pick<RegionTask, 'label' | 'rank'> {
  children: RestrictionListTask[];
  identifyBodyLocation?: boolean;
  bodyLocationValue?: IdentifyBodyLocationEnum;
  error?: string;
}

export interface TimeRestrictionTask extends Omit<RestrictionListTask, 'measureUnit'> {
  measureUnit: MeasureUnitValues;
  taskValueError?: string;
}

export interface WeightRestrictionChildTask extends RestrictionListTask {
  weightRestriction: {
    maxRange: number;
    header: string;
  };
}

export interface WeightRestrictionParentTask extends RestrictionListParentTask {
  weightRestrictionMaxRange: number;
  showChildSelectors: boolean;
  children: WeightRestrictionChildTask[]; // Child that's been selected
  maxWeightRangeError?: string;
}

export interface YesNoRestrictionTask extends Omit<RestrictionListTask, 'measureUnit' | 'restrictedValue'> {
  measureUnit: MeasureUnitValues.Bool;
  restrictedValue: 'No';
}

type UpperOrWholeBodyTask = TimeRestrictionTask & {
  label: `Push/Pull ${'Upper' | 'Whole'} Body ${string}kg`;
};

// **** Functions **** //

/**
 * Check if a task is a Parent Task
 */
export const isRestrictionListTask = (arg: RestrictionListItem): arg is RestrictionListTask => {
  return !('children' in arg);
};

/**
 * Is a TimeRestrictionTask
 */
export const isTimeRestrictionTask = (arg: RestrictionListItem): arg is TimeRestrictionTask => {
  return isRestrictionListTask(arg) && arg.measureUnit === MeasureUnitValues.Hours;
};

/**
 * Check if a task is a Parent Task
 */
export const isParentTask = (arg: RestrictionListItem): arg is RestrictionListParentTask => {
  return 'children' in arg;
};

/**
 * Check if a task is a WeightRestrictionChildTask
 */
export const isWeightRestrictionChildTask = (arg: RestrictionListItem): arg is WeightRestrictionChildTask => {
  return isTimeRestrictionTask(arg) && 'weightRestriction' in arg;
};

/**
 * Check if a task is a WeightRestrictionParentTask
 */
export const isWeightRestrictionParentTask = (arg: RestrictionListItem): arg is WeightRestrictionParentTask => {
  return isParentTask(arg) && 'weightRestrictionMaxRange' in arg;
};

/**
 * Is a task a boolean Restriction Type task.
 */
export const isYesNoRestrictionTask = (arg: RestrictionListItem): arg is YesNoRestrictionTask => {
  return isRestrictionListTask(arg) && arg.measureUnit === MeasureUnitValues.Bool;
};

/**
 * Check if upper or whole body type task (i.e. Push/Pull Upper Body <5kg)
 */
const isUpperOrWholeBodyTask = (arg: RestrictionListItem): arg is UpperOrWholeBodyTask => {
  return (
    isTimeRestrictionTask(arg) &&
    arg.label.startsWith('Push/Pull') &&
    (arg.label.includes('Upper Body') || arg.label.includes('Whole Body'))
  );
};

/**
 * Consolidate tasks with that are supposed to have child tasks (i.e. Push/Pull and Lift/Carry).
 */
export const regionTaskListToRestrictionItemTasks = (
  tasks: RegionTask[],
): (RestrictionListTask | RestrictionListParentTask)[] => {
  // Filter Lift/Carry
  const liftCarryTasks: WeightRestrictionChildTask[] = tasks
    .filter((t) => t.label.startsWith('Lift/Carry'))
    .map((t) => ({ ...t, weightRestriction: { header: '', maxRange: 0 } }));
  // Filter Push/Pull
  const pushPullTasks: WeightRestrictionChildTask[] = tasks
    .filter((t) => t.label.startsWith('Push/Pull') && !isUpperOrWholeBodyTask(t))
    .map((t) => ({ ...t, weightRestriction: { header: '', maxRange: 0 } }));
  // Filter the rest
  const takenSet = new Set([...pushPullTasks.map((t) => t.taskId), ...liftCarryTasks.map((t) => t.taskId)]);
  const retVal: (RestrictionListTask | RestrictionListParentTask)[] = tasks.filter((t) => !takenSet.has(t.taskId));
  // Add the parents to the final array
  if (liftCarryTasks.length > 0) {
    const parent = initWeightRestrictionParentTask('Lift/Carry', liftCarryTasks);
    retVal.push(parent);
  }
  if (pushPullTasks.length > 0) {
    const parent = initWeightRestrictionParentTask('Push/Pull', pushPullTasks);
    retVal.push(parent);
  }
  // Return
  return retVal;
};

/**
 * Initialize parent tasks related to weight.
 * Notes:
 *  - If any child has .identifyBodyLocation = true, then it needs to be true for the parent as well.
 *  - The parent's rank is based on the rank of the first child in the list so it can be sorted.
 */
const initWeightRestrictionParentTask = (
  parentLabel: string,
  childTasks: WeightRestrictionChildTask[],
): RestrictionListParentTask => {
  // Init parent
  const parentTask: WeightRestrictionParentTask = {
    label: parentLabel,
    rank: 0,
    weightRestrictionMaxRange: WEIGHT_RESTRICTION_RANGE_NOT_SET,
    children: [],
    showChildSelectors: false,
  };
  // Initialize child-tasks
  for (const childTask of childTasks) {
    if (childTask.identifyBodyLocation) {
      parentTask.identifyBodyLocation = true;
    }
    const option = WeightRestrictionMaxRangeHandler.getChildOption(childTask.label);
    childTask.weightRestriction = {
      maxRange: option.value,
      header: option.header,
    };
  }
  // Sort children / add rank
  parentTask.children = childTasks.sort((a, b) => {
    const aValue = a.weightRestriction.maxRange;
    const bValue = b.weightRestriction.maxRange;
    return aValue - bValue;
  });
  // Set the parent rank
  parentTask.rank = parentTask.children[0].rank;
  // Return
  return parentTask;
};

/**
 * Look an incoming list of selected-tasks and set the restriction-list items accordingly
 */
export const setupPreselectedRestrictionListItems = (
  preSelectedTasks: PreSelectedTask[],
  consolidatedRegionTasks: (RestrictionListTask | RestrictionListParentTask)[],
) => {
  // Set the restrictionListItems from the consolidated-tasks and selected-tasks
  const preSelectedTaskMap = new Map(preSelectedTasks.map((t) => [t.label, t]));
  const selectedConsolidatedTasks = consolidatedRegionTasks.filter((task) => {
    if (!isParentTask(task)) {
      return preSelectedTaskMap.has(task.label);
    } else {
      return task.children.some((child) => preSelectedTaskMap.has(child.label));
    }
  });
  const restrictionListItems = _.cloneDeep(selectedConsolidatedTasks);
  // Set the values for normal tasks
  restrictionListItems.filter(isRestrictionListTask).map((task) => {
    const preSelectedTask = preSelectedTaskMap.get(task.label);
    task.restrictedValue ??= preSelectedTask?.restrictedValue;
    task.bodyLocationValue ??= preSelectedTask?.bodyLocationValue;
  });
  // Set values for weight restriction parent tasks
  restrictionListItems.filter(isWeightRestrictionParentTask).map((parent) => {
    parent.children.forEach((child, i) => {
      const preSelectedTask = preSelectedTaskMap.get(child.label);
      child.restrictedValue ??= preSelectedTask?.restrictedValue;
      child.bodyLocationValue ??= preSelectedTask?.bodyLocationValue;
      parent.bodyLocationValue ??= preSelectedTask?.bodyLocationValue;
      if (isNotEmpty(child.restrictedValue) && child.restrictedValue !== '0') {
        parent.showChildSelectors = true;
      }
      if (parent.weightRestrictionMaxRange === WEIGHT_RESTRICTION_RANGE_NOT_SET && child.restrictedValue === '0') {
        if (i > 0) {
          parent.weightRestrictionMaxRange = parent.children[i - 1].weightRestriction.maxRange;
        } else {
          parent.weightRestrictionMaxRange = 0;
        }
      }
    });
    // Edge case for when max range is selected
    if (parent.weightRestrictionMaxRange === WEIGHT_RESTRICTION_RANGE_NOT_SET) {
      const lastChild = parent.children[parent.children.length - 1];
      if (isNotEmpty(lastChild.restrictedValue) && lastChild.restrictedValue !== '0') {
        parent.weightRestrictionMaxRange = lastChild.weightRestriction.maxRange;
      }
    }
  });
  // Order the restrictionListItems to match the preSelected tasks
  const retVal: (RestrictionListTask | RestrictionListParentTask)[] = [];
  preSelectedTasks.forEach((preSelectedTask) => {
    const index = restrictionListItems.findIndex((item) => {
      return !isParentTask(item)
        ? item.label === preSelectedTask.label
        : item.children.some((child) => child.label === preSelectedTask.label);
    });
    if (index !== -1) {
      const taskToAdd = restrictionListItems[index];
      if (isParentTask(taskToAdd)) {
        restrictionListItems.splice(index, 1);
      }
      retVal.push(taskToAdd);
    }
  });
  // Set the restriction-list-items
  return retVal;
};

/**
 * If a placement was created before the new.
 */
export const validateIncomingPreSelectedRestrictionItems = (
  preselectedRestrictionListItems: (RestrictionListTask | RestrictionListParentTask)[],
  shiftLength: number,
): boolean => {
  // Don't check "UpperOrWholeBodyTask" tasks
  const tasks = preselectedRestrictionListItems.filter((t) => !isUpperOrWholeBodyTask(t));
  // Check non-parent tasks
  const nonParentTasks = tasks.filter(isRestrictionListTask);
  let hasInvalid = nonParentTasks.some((t) => !validateRestrictedValue(t, shiftLength));
  if (hasInvalid) {
    return false;
  }
  // Check consolidated tasks (i.e. push/pull and lift/carry)
  const parentTasks = tasks.filter(isWeightRestrictionParentTask);
  const childTasks = _.flatMap(parentTasks, (t) => t.children);
  hasInvalid = childTasks.some((t) => (isEmpty(t.restrictedValue) ? false : !validateRestrictedValue(t, shiftLength)));
  if (hasInvalid) {
    return false;
  }
  // Check child values of a parent-weight-restriction-tasks
  hasInvalid = parentTasks.some((t) => !validateChildWeightTaskValues(t));
  return !hasInvalid;
};

/**
 * Validate a restricted value.
 */
const validateRestrictedValue = (task: RestrictionListTask, shiftLength: number): boolean => {
  if (task.measureUnit === MeasureUnitValues.Hours) {
    const value = Number(task.restrictedValue);
    return !isNaN(value) && Number.isInteger(value) && value >= 0 && value < shiftLength;
  } else if (task.measureUnit === MeasureUnitValues.Bool) {
    return task.restrictedValue === 'No';
  } else {
    return task.restrictedValue !== undefined;
  }
};

/**
 * Validate the children restricted values.
 */
const validateChildWeightTaskValues = (task: WeightRestrictionParentTask): boolean => {
  for (let i = 0; i < task.children.length; i++) {
    const childTask = task.children[i];
    // All child tasks greater than the max range must be 0
    if (childTask.weightRestriction.maxRange > task.weightRestrictionMaxRange && childTask.restrictedValue !== '0') {
      return false;
    }
    // All child tasks higher up in the max-range ranking must have a value "equal to or lower than"
    // their preceeding sibling.
    const childTaskValue = restrictedValueToNumber(childTask.restrictedValue);
    for (let j = i + 1; j < task.children.length; j++) {
      const nextChild = task.children[j];
      const nextChildValue = restrictedValueToNumber(nextChild.restrictedValue);
      if (nextChildValue > childTaskValue) {
        return false;
      }
    }
  }
  return true;
};

/**
 * Validate the list of restriction items
 */
export const validateRestrictionListItems = (
  list: RestrictionListItem[],
): { isValid: boolean; updatedList: RestrictionListItem[] } => {
  let isValid = true;
  for (const task of list) {
    delete task.error;
    if (isWeightRestrictionParentTask(task) && !validateConsolidatedWeightTask(task)) {
      isValid = false;
    } else if (isTimeRestrictionTask(task) && !isNotEmpty(task.restrictedValue)) {
      isValid = false;
      task.taskValueError = VALIDATION_ERRORS.NoTaskValue;
    }
  }
  return { isValid, updatedList: list };
};

/**
 * Validate a consolidated weight restriction parent and children.
 */
const validateConsolidatedWeightTask = (task: WeightRestrictionParentTask): boolean => {
  if (task.weightRestrictionMaxRange === WEIGHT_RESTRICTION_RANGE_NOT_SET) {
    task.maxWeightRangeError = VALIDATION_ERRORS.MaximumWeightRangeRequired;
    return false;
  }
  return !task.children.some((c) => !!c.error);
};

/**
 * Validate the children. A higher ranked child's restricted value cannot be greater
 * than a lower ranked child's restricted value.
 */
export const validateConsolidatedWeightTaskChildren = (
  task: WeightRestrictionParentTask,
): WeightRestrictionParentTask => {
  task.children.forEach((c) => delete c.error);
  for (let i = 0; i < task.children.length; i++) {
    const childValue = task.children[i].restrictedValue ?? Number.MAX_SAFE_INTEGER;
    for (let j = i + 1; j < task.children.length; j++) {
      const nextChild = task.children[j];
      const nextChildValue = nextChild.restrictedValue ?? Number.MAX_SAFE_INTEGER;
      if (nextChildValue > childValue) {
        nextChild.error = VALIDATION_ERRORS.NextRangeValueTooHigh;
      }
    }
  }
  return task;
};

/**
 * Convert the restrictedValue to a number so it can be used for comparison
 */
const restrictedValueToNumber = (restrictedValue: unknown) =>
  isEmpty(restrictedValue) ? Number.MAX_SAFE_INTEGER : Number(restrictedValue);

/**
 * Determine the shift-length from the "backgroundFormData."
 */
export const setupShiftLength = (backgroundFormData: BackgroundFormData): number => {
  const shiftLength = backgroundFormData.cannotWorkNormalShiftHrs
    ? backgroundFormData.limitedHoursPerDay
    : backgroundFormData.shiftLength;
  return Number(shiftLength);
};

/**
 * Get the restricted value appended with the abbreviation for measureUnit
 * i.e. => '20 lbs'
 */
export const getRestrictedValueFull = (restrictionListItem: RestrictionListTask, shiftLength?: number): string => {
  const { restrictedValue, measureUnit } = restrictionListItem;
  const value = isEmpty(restrictedValue) && isNotEmpty(shiftLength) ? shiftLength : restrictedValue;
  return `${value} ${MeasureUnitAbbreviations[measureUnit]}`;
};
